import ThemeToggle from './ThemeToggle';
import { clearAccountSession } from '../utils/sessionCache';
import { AccountAvatar } from './PageHeading';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SavedFilterCard from './SavedFilterCard';
import { notificationApi } from '../utils/newsNotifications';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

function LogoTray() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem('token'));
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('isAdmin') === 'true');
  const [username, setUsername] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.username || '';
    } catch { return ''; }
  });
  const [darkTheme, setDarkTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'true' : true;
  });

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterName, setActiveFilterName] = useState(null);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileUserSubmenu, setShowMobileUserSubmenu] = useState(false);

  useEffect(() => {
    if (!window.jaosveNative) return undefined;
    const dismiss = event => {
      if (!(showFiltersDropdown || showAccountDropdown || showMobileMenu || showMobileUserSubmenu)) return;
      event.preventDefault();
      setShowFiltersDropdown(false);
      setShowAccountDropdown(false);
      setShowMobileMenu(false);
      setShowMobileUserSubmenu(false);
    };
    window.addEventListener('jaosve-dismiss-overlays', dismiss);
    return () => window.removeEventListener('jaosve-dismiss-overlays', dismiss);
  }, [showFiltersDropdown, showAccountDropdown, showMobileMenu, showMobileUserSubmenu]);

  const containerRef = useRef(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const refreshNotifications = useCallback(async () => {
    if (localStorage.getItem('token')) setSubscriptions(await notificationApi());
  }, []);
  useEffect(() => {
    if (!loggedIn) return;
    const refresh = () => refreshNotifications().catch(() => {});
    refresh();
    window.addEventListener('news-notifications-changed', refresh);
    return () => window.removeEventListener('news-notifications-changed', refresh);
  }, [loggedIn, refreshNotifications]);
  const onFilterChanged = async (updated, previous) => {
    const response = await fetch(`${API_BASE_URL}/favorite-filters`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!response.ok) throw new Error('Filteri trenutno nisu dostupni.');
    setSavedFilters(await response.json());
    if (previous.name === activeFilterName) {
      setActiveFilterName(updated?.name || null);
      if (updated) localStorage.setItem('currentFilter', JSON.stringify(updated));
      else localStorage.removeItem('currentFilter');
    }
    await refreshNotifications();
  };


  // Apply theme to html and body class
  useEffect(() => {
    document.body.classList.toggle('dark-theme', darkTheme);
    document.body.classList.toggle('light-theme', !darkTheme);
    document.documentElement.classList.toggle('dark-theme', darkTheme);
    document.documentElement.classList.toggle('light-theme', !darkTheme);
    localStorage.setItem('theme', String(darkTheme));
  }, [darkTheme]);

  // Fetch saved filters and active filter when logged in
  useEffect(() => {
    if (!loggedIn) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsLoadingFilters(true);
    fetch(`${API_BASE_URL}/favorite-filters`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (Array.isArray(data)) setSavedFilters(data);
      })
      .catch(() => {})
      .finally(() => setIsLoadingFilters(false));

    try {
      const cf = JSON.parse(localStorage.getItem('currentFilter'));
      if (cf?.name) setActiveFilterName(cf.name);
    } catch { /* ignore */ }
  }, [loggedIn]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (e.target.closest('.control-help-popover')) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowFiltersDropdown(false);
        setShowAccountDropdown(false);
        setShowMobileMenu(false);
        setShowMobileUserSubmenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleToggleTheme = () => {
    setDarkTheme((v) => !v);
  };

  const handleLogout = useCallback(() => {
    clearAccountSession();
    setSavedFilters([]);
    setActiveFilterName(null);
    setShowFiltersDropdown(false);
    setLoggedIn(false);
    setIsAdmin(false);
    setUsername('');
    setShowAccountDropdown(false);
    setShowMobileMenu(false);
    navigate('/');
  }, [navigate]);

  const handleDropdownClick = (which) => {
    if (which === 'filters') {
      setShowFiltersDropdown((v) => !v);
      setShowAccountDropdown(false);
    } else if (which === 'account') {
      setShowMobileUserSubmenu(false);
      setShowAccountDropdown((v) => !v);
      setShowFiltersDropdown(false);
    }
  };

  const applySavedFilter = async (filter) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/current-filter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(filter),
        });
      } catch {
        // ignore - will still save locally and navigate
      }
    }
    localStorage.setItem('currentFilter', JSON.stringify(filter));
    if (filter.name) setActiveFilterName(filter.name);
    setShowFiltersDropdown(false);
    setShowMobileMenu(false);
    navigate('/');
  };

  const isActive = (target) => {
    if (target === '/') return pathname === '/';
    if (target === '/postavke') return pathname === '/postavke';
    return pathname === target;
  };

  return (
    <div className="logo-tray" ref={containerRef}>
      <div
        className="logo-container"
        onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        style={{ cursor: 'pointer' }}
      >
        <div className="tech-logo">
          <img
            src={darkTheme ? '/logo-dark.png' : '/logo-light.png'}
            alt="JaoSVE!"
            className="logo-image"
          />
        </div>
      </div>
      <div className="logo-space"></div>

      {/* Mobile tray actions - centered theme toggle + filter icon */}
      <div className="mobile-tray-actions">
        <button
          onClick={handleToggleTheme}
          className="mobile-tray-btn mobile-theme-toggle-tray"
          aria-label={darkTheme ? 'Svijetla tema' : 'Tamna tema'}
          title={darkTheme ? 'Svijetla tema' : 'Tamna tema'}
        >
          {darkTheme ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        {loggedIn && (
          <div className="mobile-filter-wrap">
            <button
              onClick={() => handleDropdownClick('filters')}
              className="mobile-tray-btn mobile-filter-toggle-tray"
              aria-label="Moji filteri"
              title="Moji filteri"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
            {showFiltersDropdown && (
              <div className="filter-dropdown mobile-filter-dropdown">
                <div className="saved-filters">
                  <h4>Spašeni filteri</h4>
                  {isLoadingFilters ? (
                    <p>Učitavanje filtera...</p>
                  ) : savedFilters.length === 0 ? (
                    <p>Nema spremljenih filtera</p>
                  ) : (
                    [...savedFilters]
                      .map((filter, index) => ({ filter, index }))
                      .reverse()
                      .map(({ filter, index }) => (
                        <SavedFilterCard key={filter.id || index} filter={filter} index={index} active={filter.name === activeFilterName}
                          subscription={subscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))}
                          onApply={applySavedFilter} onChanged={onFilterChanged} onNotificationsChanged={refreshNotifications} />
                      ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="logo-space logo-space-right"></div>

      <div className="user-settings-placeholder">
        {/* Desktop buttons */}
        <div className="desktop-buttons">


          <div className="nav-tabs">
            <div className="nav-tab-item">
              <button
                onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`filter-button${isActive('/') ? ' active' : ''}`}
              >
                Početna
              </button>
            </div>

            <div className="nav-tab-item">
              <button
                onClick={() => navigate('/about')}
                className={`filter-button${isActive('/about') ? ' active' : ''}`}
              >
                O nama
              </button>
            </div>

            <div className="nav-tab-item">
              <button
                onClick={() => navigate('/how-to-use')}
                className={`filter-button${isActive('/how-to-use') ? ' active' : ''}`}
              >
                Kako koristiti
              </button>
            </div>

            {loggedIn && (
              <>
                {isAdmin && (
                  <div className="nav-tab-item">
                    <button
                      onClick={() => navigate('/admin')}
                      className={`filter-button${isActive('/admin') ? ' active' : ''}`}
                    >
                      Admin Panel
                    </button>
                  </div>
                )}
                <div className="nav-tab-item">
                  <button
                    onClick={() => handleDropdownClick('filters')}
                    aria-expanded={showFiltersDropdown}
                    className="filter-button saved-filters-trigger"
                  >
                    Moji filteri
                  </button>
                  {showFiltersDropdown && (
                    <div className="filter-dropdown">
                      <div className="saved-filters">
                        <h4>Spašeni filteri</h4>
                        {isLoadingFilters ? (
                          <p>Učitavanje filtera...</p>
                        ) : savedFilters.length === 0 ? (
                          <p>Nema spremljenih filtera</p>
                        ) : (
                          [...savedFilters]
                            .map((filter, index) => ({ filter, index }))
                            .reverse()
                            .map(({ filter, index }) => (
                              <SavedFilterCard key={filter.id || index} filter={filter} index={index} active={filter.name === activeFilterName}
                          subscription={subscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))}
                          onApply={applySavedFilter} onChanged={onFilterChanged} onNotificationsChanged={refreshNotifications} />
                            ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                          <ThemeToggle darkTheme={darkTheme} onClick={handleToggleTheme} />
                <div className="nav-tab-item">
                  <button
                    onClick={() => handleDropdownClick('account')}
                    className={`filter-button account-trigger${isActive('/postavke') ? ' active' : ''}`}
                    title="Vaše preference"
                    aria-label={`Korisnički meni: ${username}`} aria-expanded={showAccountDropdown}
                  >
                    <AccountAvatar username={username} />
                  </button>
                  {showAccountDropdown && (
                    <div className="account-dropdown">
                      <button onClick={() => { setShowAccountDropdown(false); navigate('/postavke'); }}>
                        Postavke
                      </button>
                      <button onClick={handleLogout}>
                        Odjavi se
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {!loggedIn && <ThemeToggle darkTheme={darkTheme} onClick={handleToggleTheme} />}
            {!loggedIn && (
              <>
                <button
                  onClick={() => navigate('/signup')}
                  className={`filter-button${isActive('/signup') ? ' active' : ''}`}
                >
                  Registruj se
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className={`header-login-btn${isActive('/login') ? ' active' : ''}`}
                >
                  Prijavi se
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile hamburger menu */}
        <div className="mobile-menu">
          {loggedIn && (
              <div className="mobile-account-wrap">
                <button className="mobile-account-trigger" onClick={() => { setShowMobileUserSubmenu((v) => !v); setShowMobileMenu(false); setShowFiltersDropdown(false); setShowAccountDropdown(false); }} aria-label={`Korisnički meni na manjim ekranima: ${username}`} aria-expanded={showMobileUserSubmenu}>
                  <AccountAvatar username={username} />
                </button>
                {showMobileUserSubmenu && (
                  <div className="account-dropdown mobile-account-dropdown">
                    <button onClick={() => { setShowMobileUserSubmenu(false); navigate('/postavke'); }}>Postavke</button>
                    <button onClick={handleLogout}>Odjavi se</button>
                  </div>
                )}
              </div>
            )}
            <button className="hamburger-button" onClick={() => { setShowMobileMenu((v) => !v); setShowMobileUserSubmenu(false); setShowFiltersDropdown(false); setShowAccountDropdown(false); }} aria-label="Meni" aria-expanded={showMobileMenu}>
              <span className={`hamburger-icon${showMobileMenu ? ' open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          {showMobileMenu && (
            <div className="mobile-dropdown">
              <button
                className={isActive('/') ? 'active' : ''}
                onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); setShowMobileMenu(false); }}
              >
                Početna
              </button>
              <button
                className={isActive('/about') ? 'active' : ''}
                onClick={() => { navigate('/about'); setShowMobileMenu(false); }}
              >
                O nama
              </button>
              <button
                className={isActive('/how-to-use') ? 'active' : ''}
                onClick={() => { navigate('/how-to-use'); setShowMobileMenu(false); }}
              >
                Kako koristiti
              </button>

              {loggedIn ? (
                <>
                  {isAdmin && (
                    <button
                      className={isActive('/admin') ? 'active' : ''}
                      onClick={() => { navigate('/admin'); setShowMobileMenu(false); }}
                    >
                      Admin Panel
                    </button>
                  )}
                  <button onClick={() => handleDropdownClick('filters')}>
                    Moji filteri
                  </button>
                  {showFiltersDropdown && (
                    <div className="filter-dropdown">
                      <div className="saved-filters">
                        <h4>Spašeni filteri</h4>
                        {isLoadingFilters ? (
                          <p>Učitavanje filtera...</p>
                        ) : savedFilters.length === 0 ? (
                          <p>Nema spremljenih filtera</p>
                        ) : (
                          [...savedFilters]
                            .map((filter, index) => ({ filter, index }))
                            .reverse()
                            .map(({ filter, index }) => (
                              <SavedFilterCard key={filter.id || index} filter={filter} index={index} active={filter.name === activeFilterName}
                          subscription={subscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))}
                          onApply={applySavedFilter} onChanged={onFilterChanged} onNotificationsChanged={refreshNotifications} />
                            ))
                        )}
                      </div>
                    </div>
                  )}
                  </>
              ) : (
                <>
                  <button
                    className={isActive('/signup') ? 'active' : ''}
                    onClick={() => { navigate('/signup'); setShowMobileMenu(false); }}
                  >
                    Registruj se
                  </button>
                  <button
                    className={isActive('/login') ? 'active' : ''}
                    onClick={() => { navigate('/login'); setShowMobileMenu(false); }}
                  >
                    Prijavi se
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LogoTray;
